import { t } from "../../../i18n";

const TITLE_SELECTOR = "h1, h2, h3, h4, h5, legend, .card-title, .header-title";

function text(element: Element | null | undefined): string | undefined {
  const content = element?.textContent?.replace(/\s+/g, " ").trim();
  return content ? content : undefined;
}

function precedes(candidate: Node, reference: Node): boolean {
  return (candidate.compareDocumentPosition(reference) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
}

function labelByFor(element: HTMLElement): string | undefined {
  if (!element.id) {
    return undefined;
  }
  const labels = Array.from(element.ownerDocument.getElementsByTagName("label"));
  return text(labels.find((label) => label.htmlFor === element.id));
}

function labelByAria(element: HTMLElement): string | undefined {
  const ids = element.getAttribute("aria-labelledby");
  if (!ids) {
    return undefined;
  }
  const parts = ids
    .split(/\s+/)
    .map((id) => text(element.ownerDocument.getElementById(id)))
    .filter((part): part is string => part !== undefined);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

// A kit row holds its label as a sibling of the input's column, so the group is the unit: the
// closest label before the input (several inputs can share one group), else the group's first.
function labelByGroup(element: HTMLElement): string | undefined {
  const group = element.closest(".form-group");
  if (!group) {
    return undefined;
  }
  const labels = Array.from(group.getElementsByTagName("label"));
  const preceding = labels.filter((label) => precedes(label, element));
  return text(preceding[preceding.length - 1]) ?? text(labels[0]);
}

/** The human label of a field, falling back to its path when the markup carries none. */
export function labelFor(element: HTMLElement | undefined, path: string): string {
  if (!element) {
    return path;
  }
  return labelByFor(element) ?? labelByAria(element) ?? labelByGroup(element) ?? path;
}

/**
 * The title the chat shows for a form: an explicit `data-ai-title`, else the nearest heading
 * before the form (pages with several forms head each one), else the form's first own heading
 * (edit pages put the heading inside the form), else a positional name.
 */
export function formTitle(form: HTMLFormElement, index: number, root: ParentNode): string {
  const explicit = form.getAttribute("data-ai-title");
  if (explicit) {
    return explicit;
  }
  const headings = Array.from(root.querySelectorAll(TITLE_SELECTOR));
  const before = headings.filter((heading) => precedes(heading, form) && !heading.contains(form));
  const inside = headings.find((heading) => form.contains(heading));
  return text(before[before.length - 1]) ?? text(inside) ?? t("ai-chat.forms.untitled-form", { index: index + 1 }, "Form {{index}}");
}
